import React from 'react';
import { Action } from 'redux';
import { Template, TemplateType } from '@dev/ee-api/ee-api-models';

export type StdRawEditorPayload = {
    templateLoading?: boolean;
    templateName?: string;
    templateObject?: Template;
    templateScreenshot?: string;
    templateSaving?: boolean;
    currentBody?: string;
    pastBody?: string[];
    futureBody?: string[];
};

export interface FetchExistingTemplateStartAction extends Action {
    type: RawEditorActionTypes.FETCH_START;
}

export interface FetchExistingTemplateSuccessAction extends Action {
    type: RawEditorActionTypes.FETCH_SUCCESS;
    payload: StdRawEditorPayload & {
        isNewTemplate: boolean;
    };
}

export interface FetchExistingTemplateErrorAction extends Action {
    type: RawEditorActionTypes.FETCH_ERROR;
    payload: StdRawEditorPayload;
}

export interface GetDefaultTemplateStartAction extends Action {
    type: RawEditorActionTypes.GET_DEFAULT_TEMPLATE_START;
    payload: {
        templateType: TemplateType;
    };
}

export interface GetDefaultTemplateSuccessAction extends Action {
    type: RawEditorActionTypes.GET_DEFAULT_TEMPLATE_SUCCESS;
    payload: {
        template: Template;
        isNewTemplate: boolean;
    };
}

export interface GetDefaultTemplateErrorAction extends Action {
    type: RawEditorActionTypes.GET_DEFAULT_TEMPLATE_ERROR;
    payload: {
        isLoading: boolean;
    };
}

export interface LoadTemplateAction extends Action {
    type: RawEditorActionTypes.LOAD_TEMPLATE;
    payload: {
        templateName: string;
        isNewTemplate: boolean;
        isPublic?: boolean;
    };
}

export interface SaveTemplateStartAction extends Action {
    type: RawEditorActionTypes.SAVE_CHANGES_START;
}

export interface SaveTemplateSuccessAction extends Action {
    type: RawEditorActionTypes.SAVE_CHANGES_SUCCESS;
}

export interface SaveTemplateErrorAction extends Action {
    type: RawEditorActionTypes.SAVE_CHANGES_ERROR;
}

export interface RedoTemplateChangesAction extends Action {
    type: RawEditorActionTypes.REDO_CHANGES;
    payload: {
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface UndoTemplateChangesAction extends Action {
    type: RawEditorActionTypes.UNDO_CHANGES;
    payload: {
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ModifyTemplateNameAction extends Action {
    type: RawEditorActionTypes.CHANGE_TEMPLATE_NAME;
    payload: {
        name: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ModifyTemplateNameSuccessAction extends Action {
    type: RawEditorActionTypes.CHANGE_TEMPLATE_NAME_SUCCESS;
    payload: {
        name: string;
    };
}

export interface AddTemplateTagsAction extends Action {
    type: RawEditorActionTypes.ADD_TAG;
    payload: {
        tag: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface RemoveTemplateTagsAction extends Action {
    type: RawEditorActionTypes.REMOVE_TAG;
    payload: {
        tag: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ModifyAMPBodyAction extends Action {
    type: RawEditorActionTypes.MODIFY_AMP_BODY;
    payload: {
        ampBody: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ModifyTextBodyAction extends Action {
    type: RawEditorActionTypes.MODIFY_TEXT_BODY;
    payload: {
        textBody: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}
export interface CloseEditorAction extends Action {
    type: RawEditorActionTypes.CLOSE_EDITOR;
    payload: {
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ChangeSubjectAction extends Action {
    type: RawEditorActionTypes.MODIFY_SUBJECT;
    payload: {
        subject: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ChangePreviewTextAction extends Action {
    type: RawEditorActionTypes.MODIFY_PREVIEW_TEXT;
    payload: {
        previewText: string;
    };
}

export interface ChangeFromAction extends Action {
    type: RawEditorActionTypes.MODIFY_FROM;
    payload: {
        fromEmail: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface ChangeFromNameAction extends Action {
    type: RawEditorActionTypes.MODIFY_FROM_NAME;
    payload: {
        fromName: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}
export interface ChangeToAction extends Action {
    type: RawEditorActionTypes.MODIFY_TO;
    payload: {
        sendTo: string;
    };
}

export interface SendTestEmailAction extends Action {
    type: RawEditorActionTypes.SEND_TEST_EMAIL;
}

export interface ChangeTemplateBodyAction extends Action {
    type: RawEditorActionTypes.CHANGE_TEMPLATE_BODY;
    payload: {
        currentBody: string;
    };
}

export interface SavePastBodyAction extends Action {
    type: RawEditorActionTypes.SAVE_PAST_BODY;
    payload: {
        pastBody: string;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export interface SaveLastScreenshotAction extends Action {
    type: RawEditorActionTypes.SAVE_LAST_SCREENSHOT;
    payload: {
        lastScreenshot: string;
    };
}

export interface ChangeDefaultOptionsAction extends Action {
    type: RawEditorActionTypes.CHANGE_DEFAULT_OPTIONS;
    payload: {
        params: ChangeDefaultOptionsActionParams;
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}
export interface CloseEditorSuccessAction extends Action {
    type: RawEditorActionTypes.CLOSE_EDITOR_SUCCESS;
}

export interface SaveChangesAfterCloseEditorSuccessAction extends Action {
    type: RawEditorActionTypes.SAVE_CHANGES_AFTER_CLOSE_EDITOR_SUCCESS;
}

export interface SaveScreenshotAction extends Action {
    type: RawEditorActionTypes.SAVE_SCREENSHOT;
    payload: {
        previewRef: React.Ref<HTMLIFrameElement>;
    };
}

export type ChangeDefaultOptionsActionParams = {
    fromName?: string;
    fromEmail?: string;
    subject?: string;
    name?: string;
    previewRef?: React.Ref<HTMLIFrameElement>;
};

export interface RawEditorState {
    templateLoading: boolean;
    templateName?: string;
    templateObject?: Template;
    templateSaving: boolean;
    templateNameSaving: boolean;
    sendTo?: string;
    currentBody?: string;
    pastBody: string[];
    futureBody: string[];
    lastScreenshot?: string;
    isNewTemplate?: boolean;
    isPublic?: boolean;
    changesSaved?: boolean;
    templateType?: TemplateType;
    template?: Template;
}

export enum RawEditorActionTypes {
    ADD_TAG = '@@rawEditor/ADD_TAG',
    REMOVE_TAG = '@@rawEditor/REMOVE_TAG',
    CHANGE_TEMPLATE_NAME = '@@rawEditor/CHANGE_TEMPLATE_NAME',
    CHANGE_TEMPLATE_NAME_SUCCESS = '@@rawEditor/CHANGE_TEMPLATE_NAME_SUCCESS',
    GET_DEFAULT_TEMPLATE_START = '@@rawEditor/GET_DEFAULT_TEMPLATE_START',
    GET_DEFAULT_TEMPLATE_SUCCESS = '@@rawEditor/GET_DEFAULT_TEMPLATE_SUCCESS',
    GET_DEFAULT_TEMPLATE_ERROR = '@@rawEditor/GET_DEFAULT_TEMPLATE_ERROR',
    FETCH_SUCCESS = '@@rawEditor/FETCH_SUCCESS',
    FETCH_ERROR = '@@rawEditor/FETCH_ERROR',
    FETCH_START = '@@rawEditor/FETCH_START',
    LOAD_TEMPLATE = '@@rawEditor/LOAD_TEMPLATE',
    CHANGE_TEMPLATE_BODY = '@@rawEditor/CHANGE_TEMPLATE_BODY',
    SAVE_PAST_BODY = '@@rawEditor/SAVE_PAST_BODY',
    SAVE_CHANGES_START = '@@rawEditor/SAVE_CHANGES_START',
    SAVE_CHANGES_SUCCESS = '@@rawEditor/SAVE_CHANGES_SUCCESS',
    SAVE_CHANGES_ERROR = '@@rawEditor/SAVE_CHANGES_ERROR',
    REDO_CHANGES = '@@rawEditor/REDO_CHANGES',
    UNDO_CHANGES = '@@rawEditor/UNDO_CHANGES',
    CLOSE_EDITOR = '@@rawEditor/CLOSE_EDITOR',
    MODIFY_TO = '@@rawEditor/MODIFY_TO',
    CLOSE_EDITOR_SUCCESS = '@@rawEditor/CLOSE_EDITOR_SUCCESS',
    SAVE_CHANGES_AFTER_CLOSE_EDITOR_SUCCESS = '@@rawEditor/SAVE_CHANGES_AFTER_CLOSE_EDITOR_SUCCESS',
    MODIFY_TEXT_BODY = '@@rawEditor/MODIFY_TEXT_BODY',
    MODIFY_AMP_BODY = '@@rawEditor/MODIFY_AMP_BODY',
    MODIFY_SUBJECT = '@@rawEditor/MODIFY_SUBJECT',
    MODIFY_PREVIEW_TEXT = '@@rawEditor/MODIFY_PREVIEW_TEXT',
    MODIFY_FROM_NAME = '@@rawEditor/MODIFY_FROM_NAME',
    MODIFY_FROM = '@@rawEditor/MODIFY_FROM',
    SEND_TEST_EMAIL = '@@rawEditor/SEND_TEST_EMAIL',
    SAVE_LAST_SCREENSHOT = '@@rawEditor/SAVE_LAST_SCREENSHOT',
    CHANGE_DEFAULT_OPTIONS = '@@rawEditor/CHANGE_DEFAULT_OPTIONS',
    SAVE_SCREENSHOT = '@@raweditor/SAVE_SCREENSHOT',
}
